import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
  Stack,
  Button,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Alert,
  Checkbox,
} from '@mui/material';
import {
  List as ListIcon,
  ViewKanban as BoardIcon,
  CalendarMonth as CalendarIcon,
  Map as MapIcon,
  Add as AddIcon,
  Dashboard as WorkflowIcon,
} from '@mui/icons-material';
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useGetList,
} from 'react-admin';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { TaskDialog } from '../../components/TaskDialog';
import { FilterUserAutocomplete } from '../../components/shared/FilterUserAutocomplete';
import type { TaskFilters, TabConfig } from './types';
import { TaskListView } from './views/TaskListView';
import { TaskBoardView } from './views/TaskBoardView';
import { TaskCalendarView } from './views/TaskCalendarView';
import { TaskMapView } from './views/TaskMapView';
import type { Workflow } from '../../types/workflow.types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// localStorage key
const STORAGE_KEY = 'taskView';

// Tab configurations
const TABS: TabConfig[] = [
  { label: 'List', IconComponent: ListIcon, value: 'list', component: TaskListView },
  { label: 'Board', IconComponent: BoardIcon, value: 'board', component: TaskBoardView },
  { label: 'Calendar', IconComponent: CalendarIcon, value: 'calendar', component: TaskCalendarView },
  // { label: 'Map', IconComponent: MapIcon, value: 'map', component: TaskMapView }, // Temporarily hidden until properly supported
];

const TasksUnified: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Helper to get default date range (Current Year)
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), 0, 1), // Jan 1st
      end: new Date(now.getFullYear(), 11, 31), // Dec 31st
    };
  };

  // Helper to check if we should enforce defaults
  const shouldEnforceDefaults = (tab: string) => ['list', 'board'].includes(tab);

  // Load saved state from localStorage
  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Hydrate dates
        if (parsed.filters?.dateRange?.start) {
          parsed.filters.dateRange.start = new Date(parsed.filters.dateRange.start);
        }
        if (parsed.filters?.dateRange?.end) {
          parsed.filters.dateRange.end = new Date(parsed.filters.dateRange.end);
        }
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load saved task view state:', error);
    }
    return {
      activeTab: 'board',
      selectedWorkflow: null,
      filters: {},
    };
  };

  const savedState = loadSavedState();

  // State
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get('view') || savedState.activeTab || 'board'
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(
    searchParams.get('workflow') || savedState.selectedWorkflow || null
  );

  // Initialize filters with defaults if needed
  const initialFilters = savedState.filters || {};
  if (shouldEnforceDefaults(activeTab)) {
    const defaults = getDefaultDateRange();
    if (!initialFilters.dateRange?.start) {
      initialFilters.dateRange = { ...initialFilters.dateRange, start: defaults.start };
    }
    if (!initialFilters.dateRange?.end) {
      initialFilters.dateRange = { ...initialFilters.dateRange, end: defaults.end };
    }
  }
  const [filters, setFilters] = useState<TaskFilters>(initialFilters);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Load workflows (formerly task-boards/service lines) - only active ones
  const { data: allWorkflows = [], isLoading: workflowsLoading } = useGetList<Workflow>('workflows', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'name', order: 'ASC' },
    filter: { isActive: true },
  });

  // Filter to only show active workflows
  const workflows = allWorkflows.filter(w => w.isActive !== false);

  // If no workflow selected and workflows are loaded, select the first one
  useEffect(() => {
    if (!selectedWorkflow && workflows.length > 0) {
      setSelectedWorkflow(workflows[0].id);
    }
  }, [workflows, selectedWorkflow]);

  // Get the current workflow object
  const currentWorkflow = workflows.find(w => w.id === selectedWorkflow);

  // Load data for filters
  const { data: customers } = useGetList('customers', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'displayName', order: 'ASC' },
  });

  const { data: zones } = useGetList('crm/zones', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const { data: users } = useGetList('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Get categories from the current workflow
  const taskCategories = useMemo(() => {
    if (!currentWorkflow?.categories) return [];
    return [...currentWorkflow.categories].sort((a, b) => a.name.localeCompare(b.name));
  }, [currentWorkflow]);

  // Save state to localStorage
  const saveState = useCallback(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          activeTab,
          selectedWorkflow,
          filters,
        })
      );
    } catch (error) {
      console.error('Failed to save task view state:', error);
    }
  }, [activeTab, selectedWorkflow, filters]);

  // Save state whenever it changes
  useEffect(() => {
    saveState();
  }, [saveState]);

  // Update URL when tab or workflow changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('view', activeTab);
    if (selectedWorkflow) {
      params.set('workflow', selectedWorkflow);
    }
    setSearchParams(params, { replace: true });
  }, [activeTab, selectedWorkflow, searchParams, setSearchParams]);

  // Handle workflow change
  const handleWorkflowChange = (workflowId: string) => {
    setSelectedWorkflow(workflowId);
    // Reset status filter when changing workflow
    setFilters(prev => ({ ...prev, statusId: undefined }));
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);

    // Apply defaults if switching to list/board and dates are missing
    if (shouldEnforceDefaults(newValue)) {
      const defaults = getDefaultDateRange();
      setFilters(prev => {
        const newFilters = { ...prev };
        if (!newFilters.dateRange?.start) {
          newFilters.dateRange = { ...newFilters.dateRange, start: defaults.start };
        }
        if (!newFilters.dateRange?.end) {
          newFilters.dateRange = { ...newFilters.dateRange, end: defaults.end };
        }
        return newFilters;
      });
    }

    // Refresh data when switching tabs
    refresh();
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof TaskFilters, value: any) => {
    let newValue = value;

    // Enforce defaults if clearing dates in list/board view
    if (key === 'dateRange' && shouldEnforceDefaults(activeTab)) {
      const defaults = getDefaultDateRange();
      const newRange = { ...value }; // value is { start: ..., end: ... }

      if (!newRange.start) newRange.start = defaults.start;
      if (!newRange.end) newRange.end = defaults.end;

      newValue = newRange;
    }

    setFilters(prev => ({
      ...prev,
      [key]: newValue,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    const newFilters: TaskFilters = {};

    // Enforce defaults if clearing in list/board view
    if (shouldEnforceDefaults(activeTab)) {
      newFilters.dateRange = getDefaultDateRange();
    }

    setFilters(newFilters);
  };

  // Count active filters (excluding workflowId as it's always selected)
  const activeFilterCount = Object.entries(filters)
    .filter(([key, value]) =>
      key !== 'workflowId' &&
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;

  // Get current tab component
  const currentTab = TABS.find(tab => tab.value === activeTab);
  const CurrentTabComponent = currentTab?.component || TaskBoardView;

  // Memoize combined filters to prevent unnecessary re-renders
  const combinedFilters: TaskFilters = useMemo(() => ({
    ...filters,
    workflowId: selectedWorkflow || undefined,
  }), [filters, selectedWorkflow]);

  if (workflowsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading workflows...</Typography>
      </Box>
    );
  }

  if (workflows.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No workflows configured. Please configure workflows in the settings.
          <Button
            sx={{ ml: 2 }}
            variant="outlined"
            size="small"
            onClick={() => navigate('/configuration/workflows')}
          >
            Configure Workflows
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Compact Header with Filters */}
        <Paper sx={{ p: 1.5, borderRadius: 0, boxShadow: 1 }}>
          <Stack spacing={1.5}>
            {/* Top row: Workflow selector and Create button */}
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <FormControl sx={{ minWidth: 250 }} size="small">
                <Select
                  value={selectedWorkflow}
                  onChange={(e) => handleWorkflowChange(e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 600,
                    },
                  }}
                >
                  {workflows.map((workflow) => (
                    <MenuItem key={workflow.id} value={workflow.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: workflow.color,
                          }}
                        />
                        {workflow.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                size="small"
              >
                Create Task
              </Button>
            </Stack>

            {/* Filters row */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">

              {/* Status filter - specific to current workflow */}
              {currentWorkflow && (
                <FormControl sx={{ minWidth: 150 }} size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.statusId || ''}
                    onChange={(e) => handleFilterChange('statusId', e.target.value || undefined)}
                    input={<OutlinedInput label="Status" />}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {currentWorkflow.statuses.map(status => (
                      <MenuItem key={status.id} value={status.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: status.color,
                            }}
                          />
                          {status.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}


              {customers && (
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => option.displayName || option.company || ''}
                  value={customers.find(c => c.id === filters.customerId) || null}
                  onChange={(_, value) => handleFilterChange('customerId', value?.id || undefined)}
                  renderInput={(params) => (
                    <TextField {...params} label="Customer" size="small" />
                  )}
                  sx={{ minWidth: 200 }}
                  size="small"
                />
              )}

              {taskCategories.length > 0 && (
                <Autocomplete
                  options={taskCategories}
                  getOptionLabel={(option) => option.name || ''}
                  value={taskCategories.find(c => c.id === filters.categoryId) || null}
                  onChange={(_, value) => handleFilterChange('categoryId', value?.id || undefined)}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" size="small" />
                  )}
                  sx={{ minWidth: 150 }}
                  size="small"
                />
              )}

              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority || ''}
                  onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                  input={<OutlinedInput label="Priority" />}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {['Low', 'Medium', 'High', 'Urgent'].map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {users && (
                <FilterUserAutocomplete
                  users={users}
                  value={filters.assigneeId}
                  onChange={(userId) => handleFilterChange('assigneeId', userId)}
                  label="Assignee"
                  size="small"
                  sx={{ minWidth: 180 }}
                />
              )}

              {activeTab !== 'calendar' && (
                <>
                  <DatePicker
                    label="Start Date"
                    value={filters.dateRange?.start || null}
                    onChange={(newValue: Date | null) => handleFilterChange('dateRange', { ...filters.dateRange, start: newValue })}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                    enableAccessibleFieldDOMStructure={false}
                  />

                  <DatePicker
                    label="End Date"
                    value={filters.dateRange?.end || null}
                    onChange={(newValue: Date | null) => handleFilterChange('dateRange', { ...filters.dateRange, end: newValue })}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                </>
              )}

              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`}
                  onDelete={handleClearFilters}
                  color="primary"
                  size="small"
                />
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 0, boxShadow: 0, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ minHeight: 40 }}>
            {TABS.map(tab => {
              const IconComponent = tab.IconComponent;
              return (
                <Tab
                  key={tab.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconComponent sx={{ fontSize: 18 }} />
                      {tab.label}
                    </Box>
                  }
                  value={tab.value}
                />
              );
            })}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {currentWorkflow ? (
            <CurrentTabComponent
              filters={combinedFilters}
              selectedWorkflow={currentWorkflow}
              onClearFilters={handleClearFilters}
            />
          ) : (
            <Alert severity="info">
              Please select a workflow to view tasks
            </Alert>
          )}
        </Box>

        {/* Task Dialog (Create) */}
        <TaskDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          workflowId={selectedWorkflow || undefined}
          onSuccess={() => {
            refresh();
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export { TasksUnified };